// ============================================
// AI Twin Client Service
// ============================================
//
// Client-side service that the TwinRunner UI calls.
// Orchestrates twin conversations using:
//   - claudeProxy (Cloud Function) for AI Twin (Claude)
//   - Gemini Flash for AI Nurse
//   - claudeProxy for conversation scoring
// ============================================

import type {
  TestPersona,
  BehaviorModel,
  Touchpoint,
  Message,
  CheckResult,
  RegisterType,
} from '../../types/patientJourney';
import {
  runTwinConversation,
  runTwinConversationV3,
  type ConversationCallbacks,
  type TwinConversationResult,
} from './twinEngine';
import type { PJEConfig } from './pjeConfig';
import { claudeChat, claudeGenerate } from '../ai/providers/claude';
import { geminiChat } from '../ai/providers/gemini';

// --- Constants ---

const TWIN_MODEL = 'claude-sonnet-4-5-20250929';
const NURSE_MODEL = 'gemini-2.5-flash';
const SCORER_MODEL = 'claude-sonnet-4-5-20250929';

const DEFAULT_MAX_TURNS = 20;

// --- Test Data Isolation (Section 2.3) ---
// When the real AI Nurse API exists (replacing direct Claude/Gemini calls),
// this header should be sent on every request to route to `test_patients`
// instead of `patients`. For now, we call Claude/Gemini directly so the
// header is stored as metadata only. All PJE test runs are tagged with
// `environment: 'test'` in Firestore for query-level isolation.
export const HEMOLOGIX_ENV_HEADER = 'X-HemoLogix-Environment';
export const HEMOLOGIX_ENV_VALUE = 'test';

// --- Cloud Function Twin Runner ---

import { CF_URLS } from '../../config/api';

const TWIN_RUNNER_URL = CF_URLS.runAITwin;

// --- Callback Factory ---

export type MessageCallback = (message: Message) => void;
export type CheckScoredCallback = (result: CheckResult, index: number, total: number) => void;

/**
 * Build ConversationCallbacks that use the client-side AI providers
 * (claudeProxy for Claude, Gemini SDK for nurse).
 */
function buildClientCallbacks(
  onMessage?: MessageCallback,
  onCheckScored?: CheckScoredCallback,
): ConversationCallbacks {
  return {
    onMessage,
    onCheckScored,
    callTwin: async (systemPrompt, history, nurseMessage) => {
      // Claude format: history is { role: 'user' | 'assistant', content: string }
      // claudeChat expects the same shape
      const response = await claudeChat(
        TWIN_MODEL,
        systemPrompt,
        history.map((h) => ({ role: h.role, content: h.content })),
        nurseMessage,
        0.8,  // slightly creative for natural patient responses
        1024,
      );
      return response;
    },

    callNurse: async (systemPrompt, history, patientMessage) => {
      // Gemini format: history is { role: 'user' | 'model', parts: { text }[] }
      const response = await geminiChat(
        NURSE_MODEL,
        systemPrompt,
        history.map((h) => ({
          role: h.role,
          parts: h.parts.map((p) => ({ text: p.text })),
        })),
        patientMessage,
        0.7,
        2048,
      );
      return response;
    },

    scoreCheck: async (conversationText, check) => {
      const scoringPrompt = `You are evaluating an AI nurse conversation for quality assurance.

Given the conversation below, determine whether this validation check PASSED or FAILED.

VALIDATION CHECK:
- ID: ${check.checkId}
- Check: ${check.check}
- Category: ${check.category}
- Severity: ${check.severity}

CONVERSATION:
${conversationText}

Respond with EXACTLY this format (no other text):
RESULT: YES or NO
REASONING: Brief 1-2 sentence explanation`;

      const response = await claudeGenerate(
        SCORER_MODEL,
        'You are a clinical conversation quality evaluator. Be objective and strict. Answer only in the required format.',
        scoringPrompt,
        0,
        256,
      );

      const passMatch = response.match(/RESULT:\s*(YES|NO)/i);
      const reasonMatch = response.match(/REASONING:\s*(.+)/is);

      return {
        pass: passMatch ? passMatch[1].toUpperCase() === 'YES' : false,
        reasoning: reasonMatch ? reasonMatch[1].trim() : response.trim(),
      };
    },
  };
}

// --- Public API ---

export interface TwinRunProgress {
  status: 'starting' | 'running' | 'scoring' | 'completed' | 'failed';
  currentTurn: number;
  maxTurns: number;
  currentTouchpoint?: string;
  personaName?: string;
  error?: string;
}

export type ProgressCallback = (progress: TwinRunProgress) => void;

/**
 * Run a single twin conversation for one persona at one touchpoint.
 * Uses client-side AI providers (claudeProxy + Gemini SDK).
 */
export async function runSingleTwin(
  persona: TestPersona,
  behavior: BehaviorModel,
  touchpoint: Touchpoint,
  maxTurns: number = DEFAULT_MAX_TURNS,
  onProgress?: ProgressCallback,
  onMessage?: MessageCallback,
  onCheckScored?: CheckScoredCallback,
  promptTemplate?: string,
): Promise<TwinConversationResult> {
  try {
    onProgress?.({
      status: 'starting',
      currentTurn: 0,
      maxTurns,
      currentTouchpoint: touchpoint.label,
      personaName: persona.name,
    });

    const callbacks = buildClientCallbacks(onMessage, onCheckScored);

    const result = await runTwinConversation(
      persona,
      behavior,
      touchpoint,
      maxTurns,
      callbacks,
      promptTemplate,
    );

    onProgress?.({
      status: 'completed',
      currentTurn: result.conversationLog.length,
      maxTurns,
      currentTouchpoint: touchpoint.label,
      personaName: persona.name,
    });

    return result;
  } catch (err) {
    const errorMsg = err instanceof Error ? err.message : 'Unknown error';
    onProgress?.({
      status: 'failed',
      currentTurn: 0,
      maxTurns,
      currentTouchpoint: touchpoint.label,
      personaName: persona.name,
      error: errorMsg,
    });
    throw err;
  }
}

/**
 * Run a single twin via the server-side Cloud Function.
 * Preferred for production use — avoids CORS and timeout issues.
 */
export async function runSingleTwinServerSide(
  persona: TestPersona,
  behavior: BehaviorModel,
  touchpoint: Touchpoint,
  maxTurns: number = DEFAULT_MAX_TURNS,
): Promise<TwinConversationResult> {
  const response = await fetch(TWIN_RUNNER_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      persona,
      behavior,
      touchpoint,
      maxTurns,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Twin runner error (${response.status}): ${errorText}`);
  }

  return response.json();
}

export interface FullSuiteResult {
  results: {
    personaId: string;
    personaName: string;
    touchpointId: string;
    touchpointLabel: string;
    result: TwinConversationResult;
  }[];
  overallPassRate: number;
  totalDuration: number;
  completedCount: number;
  failedCount: number;
}

/**
 * Run all personas through all their touchpoints sequentially.
 * Sequential execution to avoid rate limits.
 */
export async function runFullSuite(
  personas: TestPersona[],
  behavior: BehaviorModel,
  maxTurns: number = DEFAULT_MAX_TURNS,
  onProgress?: ProgressCallback,
): Promise<FullSuiteResult> {
  const startTime = Date.now();
  const results: FullSuiteResult['results'] = [];
  let failedCount = 0;

  for (const persona of personas) {
    for (const touchpoint of persona.journeyTouchpoints) {
      try {
        onProgress?.({
          status: 'running',
          currentTurn: 0,
          maxTurns,
          currentTouchpoint: touchpoint.label,
          personaName: persona.name,
        });

        const result = await runSingleTwin(
          persona,
          behavior,
          touchpoint,
          maxTurns,
        );

        results.push({
          personaId: persona.personaId,
          personaName: persona.name,
          touchpointId: touchpoint.touchpointId,
          touchpointLabel: touchpoint.label,
          result,
        });
      } catch (err) {
        failedCount++;
        results.push({
          personaId: persona.personaId,
          personaName: persona.name,
          touchpointId: touchpoint.touchpointId,
          touchpointLabel: touchpoint.label,
          result: {
            conversationLog: [],
            checkResults: [],
            passRate: 0,
            duration: 0,
          },
        });
      }

      // Small delay between runs to avoid rate limits
      await new Promise((r) => setTimeout(r, 1000));
    }
  }

  const totalPassed = results.reduce((sum, r) => {
    const checks = r.result.checkResults;
    return sum + checks.filter((c) => c.pass === true).length;
  }, 0);
  const totalChecks = results.reduce((sum, r) => sum + r.result.checkResults.length, 0);
  const overallPassRate = totalChecks > 0
    ? Math.round((totalPassed / totalChecks) * 100)
    : 0;

  onProgress?.({
    status: 'completed',
    currentTurn: 0,
    maxTurns,
  });

  return {
    results,
    overallPassRate,
    totalDuration: Date.now() - startTime,
    completedCount: results.length - failedCount,
    failedCount,
  };
}

// ==========================================================
// v3.1 Two-Mode Nurse Orchestration
// ==========================================================

export interface V3RunProgress extends TwinRunProgress {
  phase?: 'phase1_scripted' | 'phase2_freeform';
  activeRegister?: RegisterType;
  modifiersCollected?: number;
  sentimentDeviation?: boolean;
}

export type V3ProgressCallback = (progress: V3RunProgress) => void;

/**
 * Run a single v3.1 Two-Mode Nurse twin conversation.
 * Uses the bifurcated persona system (Analyst + Guide registers),
 * Phase 1 scripted intake → Phase 2 free-form flow.
 */
export async function runSingleTwinV3(
  persona: TestPersona,
  behavior: BehaviorModel,
  touchpoint: Touchpoint,
  maxTurns: number = DEFAULT_MAX_TURNS,
  onProgress?: V3ProgressCallback,
  onMessage?: MessageCallback,
  onCheckScored?: CheckScoredCallback,
  config?: PJEConfig,
): Promise<TwinConversationResult> {
  try {
    onProgress?.({
      status: 'starting',
      currentTurn: 0,
      maxTurns,
      currentTouchpoint: touchpoint.label,
      personaName: persona.name,
      phase: 'phase1_scripted',
      activeRegister: 'analyst',
    });

    const callbacks = buildClientCallbacks(onMessage, onCheckScored);

    // Wrap onMessage to inject v3.1 progress with phase/register info
    const v3Callbacks: ConversationCallbacks = {
      ...callbacks,
      onMessage: (msg: Message) => {
        callbacks.onMessage?.(msg);
        onProgress?.({
          status: 'running',
          currentTurn: 0, // updated by engine internally
          maxTurns,
          currentTouchpoint: touchpoint.label,
          personaName: persona.name,
          phase: msg.metadata?.registerActive ? 'phase2_freeform' : 'phase1_scripted',
          activeRegister: msg.metadata?.registerActive,
          modifiersCollected: msg.metadata?.modifiersDetected?.length,
        });
      },
    };

    const result = await runTwinConversationV3(
      persona,
      behavior,
      touchpoint,
      maxTurns,
      v3Callbacks,
      config,
    );

    onProgress?.({
      status: 'completed',
      currentTurn: result.conversationLog.length,
      maxTurns,
      currentTouchpoint: touchpoint.label,
      personaName: persona.name,
    });

    return result;
  } catch (err) {
    const errorMsg = err instanceof Error ? err.message : 'Unknown error';
    onProgress?.({
      status: 'failed',
      currentTurn: 0,
      maxTurns,
      currentTouchpoint: touchpoint.label,
      personaName: persona.name,
      error: errorMsg,
    });
    throw err;
  }
}

/**
 * v3.1 full suite — runs all personas through all touchpoints
 * using the Two-Mode Nurse conversation flow.
 */
export async function runFullSuiteV3(
  personas: TestPersona[],
  behavior: BehaviorModel,
  maxTurns: number = DEFAULT_MAX_TURNS,
  onProgress?: V3ProgressCallback,
  config?: PJEConfig,
): Promise<FullSuiteResult> {
  const startTime = Date.now();
  const results: FullSuiteResult['results'] = [];
  let failedCount = 0;

  for (const persona of personas) {
    for (const touchpoint of persona.journeyTouchpoints) {
      try {
        onProgress?.({
          status: 'running',
          currentTurn: 0,
          maxTurns,
          currentTouchpoint: touchpoint.label,
          personaName: persona.name,
          phase: 'phase1_scripted',
          activeRegister: 'analyst',
        });

        const result = await runSingleTwinV3(
          persona,
          behavior,
          touchpoint,
          maxTurns,
          undefined,
          undefined,
          undefined,
          config,
        );

        results.push({
          personaId: persona.personaId,
          personaName: persona.name,
          touchpointId: touchpoint.touchpointId,
          touchpointLabel: touchpoint.label,
          result,
        });
      } catch {
        failedCount++;
        results.push({
          personaId: persona.personaId,
          personaName: persona.name,
          touchpointId: touchpoint.touchpointId,
          touchpointLabel: touchpoint.label,
          result: {
            conversationLog: [],
            checkResults: [],
            passRate: 0,
            duration: 0,
          },
        });
      }

      // Small delay between runs to avoid rate limits
      await new Promise((r) => setTimeout(r, 1000));
    }
  }

  const totalPassed = results.reduce((sum, r) => {
    const checks = r.result.checkResults;
    return sum + checks.filter((c) => c.pass === true).length;
  }, 0);
  const totalChecks = results.reduce((sum, r) => sum + r.result.checkResults.length, 0);
  const overallPassRate = totalChecks > 0
    ? Math.round((totalPassed / totalChecks) * 100)
    : 0;

  onProgress?.({
    status: 'completed',
    currentTurn: 0,
    maxTurns,
  });

  return {
    results,
    overallPassRate,
    totalDuration: Date.now() - startTime,
    completedCount: results.length - failedCount,
    failedCount,
  };
}
